import React, { useState, useEffect } from 'react';
import {
    Button, Card, message, Typography, Space, Table, Tag, Select, DatePicker, Row, Col, Input
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import {
    notificationsService,
    NotificationDto,
    FilterOption
} from '../services/notificationsService';
import {
    rulesService,
    LoaiTbDto,
    MucDoDto
} from '../services/rulesService';
import dayjs, { Dayjs } from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Search } = Input;

const CanhBaoTab: React.FC = () => {
    const [notifications, setNotifications] = useState<NotificationDto[]>([]);
    const [filteredNotifications, setFilteredNotifications] = useState<NotificationDto[]>([]);
    const [notificationsLoading, setNotificationsLoading] = useState(false);

    const [lastRunTime, setLastRunTime] = useState<string>('');
    const [lastRunUser, setLastRunUser] = useState<string>('');

    // Filter states
    const [filterLoaiTb, setFilterLoaiTb] = useState<number | undefined>();
    const [filterMakcb, setFilterMakcb] = useState<string>('');
    const [filterManv, setFilterManv] = useState<string | undefined>();
    const [filterMakk, setFilterMakk] = useState<number | undefined>();
    const [filterMucDo, setFilterMucDo] = useState<number | undefined>();
    const [filterNgayRange, setFilterNgayRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
    const [filterTenDichVu, setFilterTenDichVu] = useState<string | undefined>();

    // Filter options
    const [loaiTbOptions, setLoaiTbOptions] = useState<LoaiTbDto[]>([]);
    const [mucDoOptions, setMucDoOptions] = useState<MucDoDto[]>([]);
    const [nhanVienOptions, setNhanVienOptions] = useState<FilterOption[]>([]);
    const [khoaPhongOptions, setKhoaPhongOptions] = useState<FilterOption[]>([]);
    const [tenDichVuOptions, setTenDichVuOptions] = useState<FilterOption[]>([]);

    useEffect(() => {
        loadLastRunInfo();
        loadNotifications();
        loadFilterOptions();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [notifications, filterLoaiTb, filterMakcb, filterManv, filterMakk, filterMucDo, filterNgayRange, filterTenDichVu]);

    const loadLastRunInfo = async () => {
        try {
            const lastRun = await rulesService.getLastRun();
            if (lastRun) {
                const runDate = new Date(lastRun.runTime);
                setLastRunTime(runDate.toLocaleString('vi-VN'));
                setLastRunUser(lastRun.username);
            }
        } catch (error: any) {
            console.error('Load last run info error:', error);
        }
    };

    const loadNotifications = async () => {
        setNotificationsLoading(true);
        try {
            const list = await notificationsService.getAll();
            setNotifications(list);
        } catch (error: any) {
            console.error('Load notifications error:', error);
            message.error('Không thể tải danh sách cảnh báo');
        } finally {
            setNotificationsLoading(false);
        }
    };

    const loadFilterOptions = async () => {
        try {
            const [loaiTb, mucDo, nhanVien, khoaPhong, tenDichVu] = await Promise.all([
                rulesService.getLoaiTb(),
                rulesService.getMucDo(),
                notificationsService.getNhanVienForFilter(),
                notificationsService.getKhoaPhongForFilter(),
                notificationsService.getTenDichVuForFilter()
            ]);
            setLoaiTbOptions(loaiTb);
            setMucDoOptions(mucDo);
            setNhanVienOptions(nhanVien);
            setKhoaPhongOptions(khoaPhong);
            setTenDichVuOptions(tenDichVu);
        } catch (error: any) {
            console.error('Load filter options error:', error);
        }
    };

    const applyFilters = () => {
        let filtered = [...notifications];

        if (filterLoaiTb !== undefined) {
            filtered = filtered.filter(n => n.maloaitb === filterLoaiTb);
        }

        if (filterMakcb) {
            filtered = filtered.filter(n => n.makcb?.toLowerCase().includes(filterMakcb.toLowerCase()));
        }

        if (filterManv !== undefined) {
            const filterStr = String(filterManv ?? '').trim();
            filtered = filtered.filter(n => {
                const nStr = String(n.manv ?? '').trim();
                return nStr.length > 0 && filterStr.length > 0 && nStr === filterStr;
            });
        }

        if (filterMakk !== undefined) {
            filtered = filtered.filter(n => n.makk === filterMakk);
        }

        if (filterMucDo !== undefined) {
            filtered = filtered.filter(n => n.mamucdo === filterMucDo);
        }

        if (filterNgayRange && filterNgayRange[0] && filterNgayRange[1]) {
            const fromDate = filterNgayRange[0].startOf('day');
            const toDate = filterNgayRange[1].endOf('day');
            filtered = filtered.filter(n => {
                if (!n.ngay) return false;
                const ngay = dayjs(n.ngay);
                return ngay.isAfter(fromDate) && ngay.isBefore(toDate);
            });
        }

        if (filterTenDichVu !== undefined) {
            filtered = filtered.filter(n => n.tendichvu === filterTenDichVu);
        }

        setFilteredNotifications(filtered);
    };

    const clearFilters = () => {
        setFilterLoaiTb(undefined);
        setFilterMakcb('');
        setFilterManv(undefined);
        setFilterMakk(undefined);
        setFilterMucDo(undefined);
        setFilterNgayRange(null);
        setFilterTenDichVu(undefined);
    };

    // Row coloring based on manhom - alternate 2 colors based on sorted manhom list
    const getRowClassName = (record: NotificationDto) => {
        if (!record.manhom) return '';
        // Get unique manhom values, sort them, and assign alternating colors
        const uniqueManhom = Array.from(new Set(filteredNotifications.map(n => n.manhom).filter((m): m is number => m !== null && m !== undefined)));
        uniqueManhom.sort((a, b) => a - b);
        const index = uniqueManhom.indexOf(record.manhom);
        return index % 2 === 0 ? 'row-color-1' : 'row-color-2';
    };

    const columns = [
        {
            title: 'Loại TB',
            dataIndex: 'tenLoaiTb',
            width: 30,
            render: (v: any) => v ? <Tag color="blue">{v}</Tag> : <Text type="secondary">-</Text>
        },
        {
            title: 'Mã KCB',
            dataIndex: 'makcb',
            width: 100,
            sorter: (a: any, b: any) => (a.makcb || '').localeCompare(b.makcb || ''),
            render: (v: any) => v ? <Text code>{v}</Text> : <Text type="secondary">-</Text>
        },
        {
            title: 'Nhân viên',
            dataIndex: 'manv',
            width: 150,
            sorter: (a: any, b: any) => {
                const na = (nhanVienOptions.find(n => String(n.value) === String(a.manv))?.label || a.manv || '').toLowerCase();
                const nb = (nhanVienOptions.find(n => String(n.value) === String(b.manv))?.label || b.manv || '').toLowerCase();
                return na.localeCompare(nb, 'vi');
            },
            render: (v: any) => {
                if (!v) return <Text type="secondary">-</Text>;
                const nv = nhanVienOptions.find(n => String(n.value) === String(v));
                const ten = nv?.label || '';
                if (ten) {
                    return (
                        <Space size={6} align="baseline" wrap>
                            <Text strong>{ten}</Text>
                            <Text code type="secondary" style={{ fontSize: 12 }}>{v}</Text>
                        </Space>
                    );
                }
                return <Text code>{v}</Text>;
            }
        },
        {
            title: 'Khoa phòng',
            dataIndex: 'makk',
            width: 110,
            render: (v: any, record: any) => {
                // Tìm tên khoa từ danh sách filter
                const khoa = khoaPhongOptions.find(k => k.value === v);
                return khoa ? khoa.label : (v ? <Text code>{v}</Text> : <Text type="secondary">-</Text>);
            }
        },
        {
            title: 'Mức độ',
            dataIndex: 'tenMucDo',
            width: 30,
            render: (v: any, record: any) => {
                const colorCode = record.colorCode || 'orange';
                return v ? <Tag color={colorCode}>{v}</Tag> : <Text type="secondary">-</Text>
            }
        },
        {
            title: 'Nội dung',
            dataIndex: 'noidung',
            width: 220,
            render: (v: any) => (
                <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {v || <Text type="secondary">-</Text>}
                </div>
            )
        },
        {
            title: 'Tên dịch vụ',
            dataIndex: 'tendichvu',
            width: 220,
            render: (v: any) => (
                <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {v || <Text type="secondary">-</Text>}
                </div>
            )
        },
        {
            title: 'Ngày',
            dataIndex: 'ngay',
            width: 110,
            sorter: (a: any, b: any) => {
                if (!a.ngay && !b.ngay) return 0;
                if (!a.ngay) return 1;
                if (!b.ngay) return -1;
                return new Date(a.ngay).getTime() - new Date(b.ngay).getTime();
            },
            render: (v: any) => v ? dayjs(v).format('DD/MM/YYYY HH:mm') : <Text type="secondary">-</Text>
        },
        {
            title: 'Ngày kết thúc',
            dataIndex: 'ngaykt',
            width: 110,
            sorter: (a: any, b: any) => {
                if (!a.ngaykt && !b.ngaykt) return 0;
                if (!a.ngaykt) return 1;
                if (!b.ngaykt) return -1;
                return new Date(a.ngaykt).getTime() - new Date(b.ngaykt).getTime();
            },
            render: (v: any) => v ? dayjs(v).format('DD/MM/YYYY HH:mm') : <Text type="secondary">-</Text>
        },
        {
            title: 'Mã nhóm',
            dataIndex: 'manhom',
            width: 30,
            defaultSortOrder: 'ascend' as any,
            sortDirections: ['ascend'] as any,
            sorter: (a: any, b: any) => {
                // Xử lý null: luôn xếp null xuống dưới cùng
                if (a.manhom == null && b.manhom == null) return 0;
                if (a.manhom == null) return 1;
                if (b.manhom == null) return -1;
                // Cả hai đều không null: sort bình thường
                return a.manhom - b.manhom;
            },
            render: (v: any) => v !== null && v !== undefined ? <Text code>{v}</Text> : <Text type="secondary">-</Text>
        }
    ];

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <style>
                {`
                    .compact-table .ant-table {
                        font-size: 10px;
                    }
                    .compact-table .ant-table-thead > tr > th {
                        font-size: 11px;
                        padding: 6px 8px;
                    }
                    .compact-table .ant-table-tbody > tr > td {
                        padding: 4px 8px;
                    }
                    .compact-table .ant-tag {
                        font-size: 10px;
                        padding: 0 4px;
                        line-height: 16px;
                    }
                    .compact-table .ant-typography {
                        font-size: 12px;
                    }
                    .compact-table .ant-typography code {
                        font-size: 12px;
                        padding: 2px 4px;
                    }
                `}
            </style>
            <Card>
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <Title level={5} style={{ margin: 0 }}>
                            Danh sách cảnh báo ({filteredNotifications.length})
                        </Title>
                        {lastRunTime && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Lần lấy dữ liệu gần nhất là {lastRunTime}, bởi {lastRunUser}
                            </Text>
                        )}
                    </Space>

                    {/* Bộ lọc */}
                    <Row gutter={[12, 12]}>
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Loại TB</Text>
                                <Select
                                    placeholder="Tất cả"
                                    allowClear
                                    style={{ width: '100%' }}
                                    value={filterLoaiTb}
                                    onChange={setFilterLoaiTb}
                                >
                                    {loaiTbOptions.map(l => (
                                        <Option key={l.maloaitb} value={l.maloaitb}>{l.tenloaitb}</Option>
                                    ))}
                                </Select>
                            </Space>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Mã KCB</Text>
                                <Search
                                    placeholder="Nhập mã KCB"
                                    allowClear
                                    value={filterMakcb}
                                    onChange={(e) => setFilterMakcb(e.target.value)}
                                    onSearch={(value) => setFilterMakcb(value)}
                                />
                            </Space>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Nhân viên (tìm theo mã HIS / tên)</Text>
                                <Select
                                    placeholder="Chọn / Tìm nhân viên"
                                    allowClear
                                    showSearch
                                    style={{ width: '100%' }}
                                    value={filterManv}
                                    onChange={setFilterManv}
                                    filterOption={(input, option) => {
                                        const val = String(option?.value ?? '');
                                        const key = String(input ?? '').trim().toLowerCase();
                                        if (!key) return true;
                                        // Dùng thuộc tính 'label' (chuỗi) đã gắn vào Option làm text tìm kiếm
                                        const labelText = String(
                                            (option as any)?.label ??
                                            val ??
                                            ''
                                        ).toLowerCase();
                                        return labelText.includes(key) || val.toLowerCase().includes(key);
                                    }}
                                    optionLabelProp="label"
                                >
                                    {nhanVienOptions.map(nv => {
                                        const vStr = String(nv.value ?? '');
                                        const labelText = `${nv.label}${vStr ? ` (${vStr})` : ''}`;
                                        return (
                                            <Option key={nv.value} value={nv.value} label={labelText}>
                                                <Space size={8} align="baseline">
                                                    <Text strong>{nv.label}</Text>
                                                    {vStr ? <Text code type="secondary" style={{ fontSize: 12 }}>{vStr}</Text> : null}
                                                </Space>
                                            </Option>
                                        );
                                    })}
                                </Select>
                            </Space>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Mã KK</Text>
                                <Select
                                    placeholder="Tất cả"
                                    allowClear
                                    showSearch
                                    style={{ width: '100%' }}
                                    value={filterMakk}
                                    onChange={setFilterMakk}
                                    optionFilterProp="label"
                                >
                                    {khoaPhongOptions.map(kp => (
                                        <Option key={kp.value} value={kp.value}>{kp.label}</Option>
                                    ))}
                                </Select>
                            </Space>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Mức độ</Text>
                                <Select
                                    placeholder="Tất cả"
                                    allowClear
                                    style={{ width: '100%' }}
                                    value={filterMucDo}
                                    onChange={setFilterMucDo}
                                >
                                    {mucDoOptions.map(md => (
                                        <Option key={md.mamucdo} value={md.mamucdo}>{md.tenmucdo}</Option>
                                    ))}
                                </Select>
                            </Space>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Ngày</Text>
                                <RangePicker
                                    style={{ width: '100%' }}
                                    value={filterNgayRange}
                                    onChange={setFilterNgayRange}
                                    format="DD/MM/YYYY"
                                />
                            </Space>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Tên dịch vụ</Text>
                                <Select
                                    placeholder="Tất cả"
                                    allowClear
                                    showSearch
                                    style={{ width: '100%' }}
                                    value={filterTenDichVu}
                                    onChange={setFilterTenDichVu}
                                    optionFilterProp="label"
                                >
                                    {tenDichVuOptions.map(td => (
                                        <Option key={td.value} value={td.value}>{td.label}</Option>
                                    ))}
                                </Select>
                            </Space>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>&nbsp;</Text>
                                <Space>
                                    <Button
                                        icon={<ReloadOutlined />}
                                        onClick={loadNotifications}
                                        loading={notificationsLoading}
                                    >
                                        Làm mới
                                    </Button>
                                </Space>
                            </Space>
                        </Col>
                    </Row>
                    <Button onClick={clearFilters} size="small">
                        Xóa bộ lọc
                    </Button>

                    <Table
                        rowKey="uniqueid"
                        size="small"
                        className="compact-table"
                        loading={notificationsLoading}
                        dataSource={filteredNotifications}
                        rowClassName={getRowClassName}
                        pagination={{
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total) => `Tổng ${total} cảnh báo`,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            defaultPageSize: 20
                        }}
                        columns={columns}
                        scroll={{ x: 1240 }}
                    />
                    <style>{`
                        .ant-table-tbody > tr.row-color-1 > td {
                            background-color: #e6f7ff;
                        }
                        .ant-table-tbody > tr.row-color-2 > td {
                            background-color: #fff7e6;
                        }
                    `}</style>
                </Space>
            </Card>
        </Space>
    );
};

export default CanhBaoTab;
